import {
  detectPlatform,
  formatWithLabels,
  formatForDisplay,
  CanonicalModifier,
  Hotkey,
  parseHotkey,
} from "@tanstack/react-hotkeys";
import { MDSShortcutKeyComboPlatform } from "./shortcut-key.types";

const SENTINEL_KEY = "K";

const normalizeTokenText = (token: string): string =>
  token.length === 1 ? token.toUpperCase() : token;

const MODIFIER_DISPLAY_ORDER: CanonicalModifier[] = ["Meta", "Control", "Alt", "Shift"];

// Component defaults to auto platform, but tests/stories can force one.
const getResolvedPlatform = (platform?: MDSShortcutKeyComboPlatform): MDSShortcutKeyComboPlatform =>
  platform ?? detectPlatform();

// "Sort" here is really an order projection: keep only modifiers present in canonical order.
const sortModifiersForDisplay = (modifiers: CanonicalModifier[]): CanonicalModifier[] =>
  MODIFIER_DISPLAY_ORDER.filter(modifier => modifiers.includes(modifier));

const formatModifierToken = ({
  modifier,
  platform,
}: {
  modifier: CanonicalModifier;
  platform: MDSShortcutKeyComboPlatform;
}): string => {
  if (modifier === "Shift") {
    return "⇧";
  }

  // Product display rule: use the command symbol on macOS, keep other modifiers as labels.
  if (platform === "mac" && modifier === "Meta") {
    const formattedCombo = formatForDisplay(`${modifier}+${SENTINEL_KEY}`, { platform });
    const formattedKey = formatForDisplay(SENTINEL_KEY, { platform });
    return (
      formattedCombo.endsWith(formattedKey)
        ? formattedCombo.slice(0, -formattedKey.length)
        : formattedCombo
    ).trim();
  }

  const formattedCombo = formatWithLabels(`${modifier}+${SENTINEL_KEY}`, { platform });
  const formattedModifier = (formattedCombo.split("+")[0] ?? formattedCombo).trim();

  if (platform === "mac" && modifier === "Control") {
    return "Ctrl";
  }

  if (platform === "mac" && modifier === "Alt") {
    return "⌥";
  }

  return formattedModifier;
};

export const getShortcutKeyComboDisplayTokens = ({
  hotkey,
  platform,
}: {
  hotkey: Hotkey;
  platform?: MDSShortcutKeyComboPlatform;
}): string[] => {
  const resolvedPlatform = getResolvedPlatform(platform);

  try {
    const parsedHotkey = parseHotkey(hotkey, resolvedPlatform);
    // Let TanStack handle special key symbols/labels (Esc, arrows, etc).
    const keyToken = normalizeTokenText(
      formatForDisplay(parsedHotkey.key, { platform: resolvedPlatform })
    );
    const modifierTokens = sortModifiersForDisplay(parsedHotkey.modifiers).map(modifier =>
      formatModifierToken({ modifier, platform: resolvedPlatform })
    );

    return [...modifierTokens, keyToken];
  } catch {
    return [normalizeTokenText(hotkey)];
  }
};
